import React, { useEffect, useState } from 'react';
import { useTutorial } from '../../contexts/TutorialContext';
import { Button } from '../ui/button';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

const PADDING = 10;
const TOOLTIP_W = 320;

function computeTooltipStyle(rect, position) {
  if (!rect) return {};
  const vw = window.innerWidth;
  const margin = 14;

  switch (position) {
    case 'right': {
      const left = rect.right + PADDING + margin;
      return {
        top: rect.top + rect.height / 2,
        left: left + TOOLTIP_W > vw - 8 ? rect.left - TOOLTIP_W - PADDING - margin : left,
        transform: 'translateY(-50%)',
      };
    }
    case 'left': {
      const left = rect.left - TOOLTIP_W - PADDING - margin;
      return {
        top: rect.top + rect.height / 2,
        left: left < 8 ? rect.right + PADDING + margin : left,
        transform: 'translateY(-50%)',
      };
    }
    case 'top':
      return {
        top: rect.top - PADDING - margin,
        left: Math.max(8, Math.min(rect.left + rect.width / 2 - TOOLTIP_W / 2, vw - TOOLTIP_W - 8)),
        transform: 'translateY(-100%)',
      };
    case 'bottom':
    default:
      return {
        top: rect.bottom + PADDING + margin,
        left: Math.max(8, Math.min(rect.left + rect.width / 2 - TOOLTIP_W / 2, vw - TOOLTIP_W - 8)),
      };
  }
}

export const TutorialOverlay = () => {
  const { isActive, currentStep, stepIndex, totalSteps, currentKey, next, prev, close, complete } =
    useTutorial();
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    if (!isActive || !currentStep?.target) {
      setTargetRect(null);
      return;
    }

    const update = () => {
      const el = document.querySelector(currentStep.target);
      if (el) {
        const rect = el.getBoundingClientRect();
        setTargetRect({ ...rect.toJSON() });
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        setTargetRect(null);
      }
    };

    update();
    const id = setInterval(update, 250);
    return () => clearInterval(id);
  }, [isActive, currentStep]);

  if (!isActive || !currentStep) return null;

  const isCentered = !targetRect;
  const position = currentStep.position || 'bottom';
  const tooltipStyle = isCentered
    ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
    : computeTooltipStyle(targetRect, position);

  return (
    <>
      {/* Dark backdrop */}
      {isCentered ? (
        <div
          className="fixed inset-0 bg-black/55 z-[9989] animate-in fade-in duration-200"
          onClick={close}
        />
      ) : (
        <svg
          className="fixed inset-0 w-full h-full z-[9989] pointer-events-none"
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}
        >
          <defs>
            <mask id="tutorial-mask">
              <rect width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - PADDING}
                  y={targetRect.top - PADDING}
                  width={targetRect.width + PADDING * 2}
                  height={targetRect.height + PADDING * 2}
                  rx="8"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.58)" mask="url(#tutorial-mask)" />
        </svg>
      )}

      {/* Spotlight border ring */}
      {targetRect && (
        <div
          className="fixed z-[9990] pointer-events-none rounded-xl"
          style={{
            top: targetRect.top - PADDING,
            left: targetRect.left - PADDING,
            width: targetRect.width + PADDING * 2,
            height: targetRect.height + PADDING * 2,
            boxShadow: '0 0 0 3px rgba(99,102,241,0.75), 0 0 20px rgba(99,102,241,0.25)',
            transition: 'all 0.25s ease',
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        className="fixed z-[9995] bg-white rounded-2xl shadow-2xl border border-slate-100 p-5 animate-in fade-in slide-in-from-bottom-2 duration-200"
        style={{ width: TOOLTIP_W, ...tooltipStyle }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          onClick={close}
          title="Chiudi (verrà mostrato di nuovo la prossima visita)"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Progress bar */}
        <div className="flex items-center gap-1.5 mb-4 pr-6">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === stepIndex
                  ? 'w-5 h-2 bg-indigo-500'
                  : i < stepIndex
                  ? 'w-2 h-2 bg-indigo-300'
                  : 'w-2 h-2 bg-slate-200'
              }`}
            />
          ))}
          <span className="ml-auto text-xs text-slate-400 tabular-nums">
            {stepIndex + 1}/{totalSteps}
          </span>
        </div>

        {/* Content */}
        <h3 className="text-base font-semibold text-slate-900 mb-1.5 leading-snug pr-4">
          {currentStep.title}
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-5">{currentStep.content}</p>

        {/* Actions */}
        <div className="flex items-center justify-between gap-2">
          <button
            className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors flex-shrink-0"
            onClick={() => complete(currentKey)}
          >
            Non mostrare più
          </button>

          <div className="flex items-center gap-2">
            {stepIndex > 0 && (
              <Button variant="outline" size="sm" onClick={prev} className="h-8 w-8 p-0">
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            <Button
              size="sm"
              onClick={next}
              className="bg-indigo-600 hover:bg-indigo-700 h-8 px-3 text-white"
            >
              {stepIndex === totalSteps - 1 ? (
                'Fine ✓'
              ) : (
                <>
                  Avanti <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
